export interface IAdminSessionRetryConfig {
  url?: string;
  _retry?: boolean;
}

export interface IAdminSessionRefreshTokens {
  access_token?: string;
  refresh_token?: string;
}

type TAdminSessionError = {
  response?: { status?: number };
  config?: IAdminSessionRetryConfig;
};

const LOGIN_PATHS = new Set(["/auth/login", "/sso/authAPI"]);

export const isAdminSessionRefreshRequest = (url?: string) =>
  typeof url === "string" && /\/auth\/req-access-token(?:\?|$)/.test(url);

export const resolveRefreshedAdminSessionTokens = (
  refreshedTokens: IAdminSessionRefreshTokens,
  currentRefreshToken?: string
): Required<Pick<IAdminSessionRefreshTokens, "access_token" | "refresh_token">> | null => {
  const accessToken = refreshedTokens.access_token;
  const refreshToken = refreshedTokens.refresh_token || currentRefreshToken;

  if (!accessToken || !refreshToken) return null;

  return {
    access_token: accessToken,
    refresh_token: refreshToken,
  };
};

export const shouldNotifyAdminSessionExpired = (
  error: TAdminSessionError,
  hasRefreshSession: boolean
): boolean => {
  if (error.response?.status !== 401 || LOGIN_PATHS.has(error.config?.url || "")) {
    return false;
  }

  return (
    !hasRefreshSession ||
    isAdminSessionRefreshRequest(error.config?.url) ||
    Boolean(error.config?._retry)
  );
};

export const shouldAttemptAdminSessionRefresh = (error: TAdminSessionError): boolean =>
  error.response?.status === 401 &&
  Boolean(error.config) &&
  !error.config?._retry &&
  !isAdminSessionRefreshRequest(error.config?.url);

export const shouldNotifyAdminSessionExpiredAfterRefreshFailure = (
  originalError: TAdminSessionError
): boolean =>
  originalError.response?.status === 401 &&
  !LOGIN_PATHS.has(originalError.config?.url || "") &&
  !isAdminSessionRefreshRequest(originalError.config?.url);
